<template>
  <Spinner v-if="loading" />
  <main class="home" v-if="!loading">
    <HeaderM3
      :logoLinkRoute="M3_Welcome"
      :showSuspiciousActivityButton="hasSuspiciousActivityMerchantFlag"
    />
    <div class="main-body d-flex flex-row">
      <div class="w-100">
        <NavigationBar :current-step="currentStep" v-show="!isEcommerceFlow" />
        <SaleTimeOut />
        <div
          class="container p-0 full-height-container d-flex flex-column h-100"
        >
          <div class="row h-100">
            <div class="col-12 h-100">
              <div
                class="row d-flex flex-row align-items-center justify-content-between mt-4 mb-4"
              >
                <div
                  class="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4"
                >
                  <h2>{{ t("ENTER_YOUR_ID_NUMBER") }}</h2>
                </div>
              </div>
              <form @submit.prevent class="h-100 d-flex flex-column">
                <div
                  class="row d-flex flex-row align-items-center justify-content-between"
                >
                  <div
                    class="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4"
                  >
                    <label for="identificationType">
                      {{ t("DOCUMENT_TYPE") }}
                    </label>
                    <select
                      class="form-control"
                      id="identificationType"
                      name="identificationType"
                      v-model="identificationType"
                      @change="onIdentificationTypeChange"
                    >
                      <option
                        v-for="idType in identificationTypes"
                        :key="idType.code"
                        :value="idType.code"
                      >
                        {{ t(idType.label) }}
                      </option>
                    </select>
                  </div>
                  <div
                    class="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4"
                  >
                    <label for="identificationNumber">
                      {{
                        t(
                          "KYC_REUSE_IDENTIFICATION_NUMBER_INPUT_LABEL_WITH_DOCUMENT_TYPE"
                        )
                      }}
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      id="identificationNumber"
                      name="identificationNumber"
                      v-model="identificationNumber"
                      @keyup.prevent="onIdentificationNumberChange"
                    />
                    <div v-if="identificationNumberError" class="text-danger">
                      {{ identificationNumberError }}
                    </div>
                  </div>
                </div>
                <div
                  class="row d-flex flex-row align-items-center justify-content-between mt-auto"
                >
                  <div
                    class="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4 mt-4"
                  >
                    <ButtonContinue
                      class="btn-next fw-bold fs-6 w-100"
                      :isEnabled="isContinueEnabled"
                      @click="onContinue"
                    >
                      <i v-if="loading" class="fa fa-spin fa-spinner"></i>
                      <span v-else>{{ t("CONTINUE") }}</span>
                    </ButtonContinue>
                    <div class="d-flex justify-content-center mt-4">
                      <button
                        type="button"
                        class="btn btn-sm btn-link pj-link-green text-decoration-none fw-bold fs-6"
                        @click.prevent="handleContinueWithPhone"
                      >
                        {{ t("CONTINUE_WITH_PHONE_NUMBER") }}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <InConsoleSidebar />
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import HeaderM3 from "@/apps/merchant3/components/HeaderM3.vue";
import { useStore } from "vuex";
import { datadogRum } from "@datadog/browser-rum";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import NavigationBar from "@/apps/merchant3/shared-components/common/NavigationBar.vue";
import SaleTimeOut from "@/apps/merchant3/shared-components/common/SaleTimeout.vue";
import {
  productVersionRecordingAppsMapper,
  recorder,
} from "@/apps/merchant3/shared-services/recording.service";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import { RoutesPageSteps } from "@/apps/merchant3/router/RoutesPageSteps";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";
import InConsoleSidebar from "@/apps/merchant3/components/in-console/ui/InConsoleSidebar.vue";
import {
  isEcommerceFlow,
  isEsignV2Country,
} from "@/apps/merchant3/helpers/checkoutFlows";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import {
  attemptKycIdNumber,
  lookupPersonByIdNumberAndType,
  getIdentificationTypes,
} from "@/apps/merchant3/services/lookup-persons.service";
import {
  validateCURP,
  ValidationResult,
} from "@/apps/merchant3/helpers/curpValidator";
import { useRouter } from "vue-router";
import { useTermsAndPrivacyAcceptance } from "@/apps/merchant3/hooks/useTermsAndPrivacyAcceptance";
import { useConfigurableNavigation } from "@/apps/merchant3/hooks/useConfigurableNavigation";
import documentTypes from "@/apps/person-registration/enums/DocumentTypes";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

const { t } = useI18n();
const M3_Welcome = ref(RouteName.M3Welcome);
document.title = "PayJoy - " + t("PAGE_TITLE_ID_NUMBER_DOB");
const store = useStore();
const currentStep = ref(1);
const app = getCurrentInstance()?.appContext.app;
const loading = ref(true);
const isCreditCheckRequired = computed(() => isEsignV2Country());
const router = useRouter();
const country = computed(() => store.state.merchant?.merchant?.country);
const validationSteps = ref(0);

// Form fields and errors
const identificationNumber = ref();
const identificationType = ref();
const identificationTypes = ref<
  Array<{ code: string; label: string; country: string }>
>([]);
const identificationNumberError = ref("");
const birthdayField = ref();
const birthdayFieldError = ref("");

/**
 * Extract date components with zero-padding
 * @param {Date} date - The date object to extract components from
 * @returns {{day: string, month: string, year: number}} Object containing padded day, month, and full year
 */
const getDateComponents = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return { day, month, year };
};

/**
 * Format date as yyyy-mm-dd required by the backend kyc-attempt endpoint
 * @param {Date} date - The date to format
 * @returns {string} The formatted date string in yyyy-mm-dd format
 */
const formatDate = (date: Date) => {
  const { day, month, year } = getDateComponents(date);
  return `${year}-${month}-${day}`;
};

/**
 * Check if the provided date indicates the user is at least 18 years old
 */
const eighteenYearsAgo = new Date();
eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

const hasSuspiciousActivityMerchantFlag = computed(() =>
  store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.SHOW_SUSPICIOUS_SALE_FLAG
  )
);
const handleContinueWithPhone = async () => {
  router.push({ name: RouteName.M3TermsAndConditions, params: { countryCode: getCountryCode() } });
};

const { handleContinueToNextPage } = useTermsAndPrivacyAcceptance();
const { isConfigurableFlow, navigateToNextStepWithRefresh } =
  useConfigurableNavigation();

const updateRecorder = () => {
  recorder(app)?.updateRecordingInfo(store.state.carts.userId, {
    cartTag: store.state.carts.cartTag,
    merchantId: store.state.carts.merchantId,
    clerkId: store.state.carts.salesClerkId,
    productVersion: store.state.carts.productVersion,
    country: store.state.merchant?.merchant?.country,
  });
};

/**
 * Validate document number using regex pattern from document types
 */
const validateDocumentNumber = (
  documentCode: string,
  documentNumber: string
): boolean => {
  const documentType = documentTypes.find((type) => type.code === documentCode);
  if (documentType?.code === "MX_NATIONAL_ID") {
    const res: ValidationResult = validateCURP(identificationNumber.value);
    return res.isValid;
  }
  if (!documentType || !documentType.regex) {
    return true; // If no regex is defined, consider it valid
  }

  const regex = new RegExp(documentType.regex);
  return regex.test(documentNumber);
};

const loadIdentificationTypes = () => {
  if (country.value) {
    try {
      identificationTypes.value = getIdentificationTypes(country.value);
      // Set the first option as selected by default
      if (identificationTypes.value && identificationTypes.value.length > 0) {
        identificationType.value = identificationTypes.value[0].code;
      }
    } catch (error) {
      console.error("Failed to load identification types:", error);
    }
  }
};

// Watch for country changes and load identification types
watch(
  country,
  (newCountry) => {
    if (newCountry) {
      console.log("Country changed to:", newCountry);
      loadIdentificationTypes();
    }
  },
  { immediate: true }
);

const dobValidated = computed(
  () =>
    birthdayField.value !== undefined &&
    birthdayField.value !== null &&
    birthdayFieldError.value === ""
);

const isContinueEnabled = computed(() => {
  return validationSteps.value === 1
    ? identificationNumberError.value === "" &&
        identificationType.value &&
        identificationNumber.value
    : validationSteps.value === 2 && dobValidated.value;
});

const resetForm = () => {
  identificationNumber.value = "";
  identificationType.value = "";
  identificationNumberError.value = "";
  birthdayField.value = undefined;
  birthdayFieldError.value = "";
  validationSteps.value = 0;
};

/**
 * Validate identification number based on country-specific rules
 */
const onIdentificationNumberChange = () => {
  identificationNumberError.value = "";
  birthdayFieldError.value = "";
  birthdayField.value = undefined;
  validationSteps.value = 0;

  if (!identificationNumber.value || identificationNumber.value.trim() === "") {
    identificationNumberError.value = t("KYC_REUSE_ERROR_ID_NUMBER_REQUIRED");
    birthdayField.value = undefined;
    return;
  }

  // Validate if identification type is selected
  if (!identificationType.value) {
    identificationNumberError.value = t("KYC_REUSE_ERROR_ID_TYPE_REQUIRED");
    birthdayField.value = undefined;
    return;
  }

  // Use regex validation for the selected document type
  const isValid = validateDocumentNumber(
    identificationType.value,
    identificationNumber.value
  );

  if (!isValid) {
    identificationNumberError.value = t(
      "KYC_REUSE_ERROR_ID_TYPE_NUMBER_REQUIRED"
    );
    birthdayField.value = undefined;
    return;
  }

  validationSteps.value = 1;
};
const onIdentificationTypeChange = () => {
  birthdayFieldError.value = "";
  birthdayField.value = undefined;
  validationSteps.value = 0;

  if (!identificationType.value || identificationType.value.trim() === "") {
    birthdayField.value = undefined;
    return;
  }

  // Revalidate identification number if it exists
  if (identificationNumber.value) {
    onIdentificationNumberChange();
  }
};

const lookupPersonByIdNumberAndTypeResponse = async (
  idNumber: string,
  idType: string
) => {
  const cartTag = store.state.carts.cartTag;
  const response = await lookupPersonByIdNumberAndType(
    cartTag,
    idNumber,
    idType
  );
  return response;
};

const handleLookupPersonByIdNumber = async () => {
  if (!identificationNumber.value) return;

  identificationNumberError.value = "";
  birthdayFieldError.value = "";

  const lookupPersonByIdNumberStart = performance.now();

  try {
    const response = await lookupPersonByIdNumberAndTypeResponse(
      identificationNumber.value,
      identificationType.value
    );
    if (response.match) {
      sessionStorage.setItem("kycReuseIdNumber", identificationNumber.value);
      sessionStorage.setItem("kycReuseOrigin", "idNumber");
      // Use configurable DOB route when in configurable flow
      const dobRoute = isConfigurableFlow.value
        ? RouteName.M3ConfigurableDob
        : RouteName.M3DateOfBirthVerification;
      router.push({ name: dobRoute, params: { countryCode: getCountryCode() } });
      return;
    } else {
      resetForm();
      // For configurable flow, use backend-driven navigation
      if (isConfigurableFlow.value) {
        await navigateToNextStepWithRefresh("POLICY");
        return;
      }
      handleContinueToNextPage();
    }
  } catch (error) {
    resetForm();
    // For configurable flow, use backend-driven navigation
    if (isConfigurableFlow.value) {
      await navigateToNextStepWithRefresh("POLICY");
      return;
    }
    handleContinueToNextPage();
  } finally {
    // Keep track of how long it took to lookup person by ID number in RUM.
    const lookupPersonByIdNumberDuration =
      performance.now() - lookupPersonByIdNumberStart;
    datadogRum.addAction("m3-api-lookup-person-by-id-number-finished", {
      loadTime: lookupPersonByIdNumberDuration,
    });
  }
};

const onContinue = async () => {
  if (loading.value) return;

  loading.value = true;
  birthdayFieldError.value = "";

  if (validationSteps.value === 1) {
    await handleLookupPersonByIdNumber();
    loading.value = false;
    return;
  }

  if (!dobValidated.value) {
    birthdayFieldError.value = t("ERROR_DATE_OF_BIRTH_REQUIRED");
    loading.value = false;
    return;
  }

  if (!identificationNumber.value) {
    validationSteps.value = 0;
    identificationNumberError.value = t("KYC_REUSE_ERROR_ID_NUMBER_REQUIRED");
    loading.value = false;
    return;
  }

  const cartTag = store.state.carts.cartTag;

  try {
    const dateObj = birthdayField.value;

    if (!dateObj) {
      birthdayFieldError.value = t("ERROR_DATE_OF_BIRTH_REQUIRED");
      loading.value = false;
      return;
    }
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      birthdayFieldError.value = t("ERROR_DATE_OF_BIRTH_INVALID");
      loading.value = false;
      return;
    }

    const dob = formatDate(dateObj);
    const result = await attemptKycIdNumber(
      cartTag,
      identificationNumber.value,
      dob
    );

    if (result.match) {
      // For configurable flow, use backend-driven navigation with config refresh
      // The refresh will get updated nextStep which accounts for SKIPPED_KYC
      if (isConfigurableFlow.value) {
        await navigateToNextStepWithRefresh("POLICY");
        return;
      }

      // Non-configurable flow: use existing frontend-driven navigation
      if (isCreditCheckRequired.value) {
        router.push({
          name: RouteName.M3CreditCheckPermissionView,
          params: { countryCode: getCountryCode() },
        });
      } else {
        router.push({
          name: RouteName.M3CustomerContactInfoView,
          params: { countryCode: getCountryCode() },
        });
      }
      return;
    } else {
      // For configurable flow, use backend-driven navigation
      if (isConfigurableFlow.value) {
        await navigateToNextStepWithRefresh("POLICY");
        return;
      }
      handleContinueToNextPage();
      return;
    }
  } catch (error) {
    // For configurable flow, use backend-driven navigation
    if (isConfigurableFlow.value) {
      await navigateToNextStepWithRefresh("POLICY");
      return;
    }
    handleContinueToNextPage();
    return;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  useCartPersistState(() => {
    store.dispatch("carts/setStep", RoutesPageSteps.IdNumberDobVerification);
    const recordingApp = productVersionRecordingAppsMapper(
      store.state.carts.productVersion
    );
    recorder(app)?.startRecording(recordingApp);
    updateRecorder();
    loadIdentificationTypes();
    loading.value = false;
    return true;
  });
});
</script>
<style scoped lang="scss">
.full-height-container {
  min-height: calc(100vh - 500px);
  display: flex;
  flex-direction: column;
}

select.form-control {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
}

select.form-control:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
</style>
